import React from 'react';
import styled from '@emotion/styled';
import { Box, CircularProgress } from '@mui/material';

// Full-page centering container. Use a <div> (not <main>) because Spinner is
// a transient overlay — it's not a page-level landmark by itself; the route's
// <main> renders once the content is ready.
const SpinnerPage = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: var(--color-bg-body);
`;

export interface SpinnerProps {
  /** Fill a page-height container. Default: true */
  fullPage?: boolean;
  /** Accessible label announced by screen readers. Default: "Loading" */
  label?: string;
}

export function Spinner({ fullPage = true, label = 'Loading' }: SpinnerProps): JSX.Element | null {
  // MUI CircularProgress already renders with role="progressbar" and the
  // correct ARIA attributes. We add aria-label so assistive technology can
  // announce what is loading rather than just "progressbar".
  const ring = (
    <CircularProgress
      size={32}
      thickness={3}
      aria-label={label}
      sx={{ color: 'var(--color-primary)' }}
    />
  );

  if (fullPage) {
    return (
      // role="status" + aria-live="polite" lets screen readers announce that
      // content is loading without interrupting whatever the user is doing.
      <SpinnerPage role="status" aria-live="polite" aria-label={label}>
        {ring}
      </SpinnerPage>
    );
  }

  return (
    <Box role="status" aria-live="polite" aria-label={label} display="inline-flex">
      {ring}
    </Box>
  );
}

export default Spinner;
